'use client';

import React, { memo, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Dropdown, type MenuProps } from 'antd';
import type { NewsFeedItem, NewsStatsItem } from '@/utils/api/news';
import { NewsEmojiInline } from './NewsEmojiInline';
import { newsArticleUrl } from './project-url';
import styles from './NewsArticleCard.module.scss';

dayjs.extend(relativeTime);
dayjs.locale('ru');

const NEW_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24h
const GLOW_THRESHOLD_MS = 60 * 60 * 1000; // 1h

export interface NewsArticleCardProps {
  item: NewsFeedItem;
  /** Скрыть бейдж проекта (для single-project ленты, чтобы не дублировать). */
  hideProjectBadge?: boolean;
  /** Вариант отображения. */
  variant?: 'grid' | 'row';
  /** Обработчик клика по карточке (например, навигация в детальный экран). */
  onClick?: (item: NewsFeedItem) => void;
  /**
   * Обработчик клика по бейджу проекта.
   * Получает (projectKey, projectId) — родитель использует key если есть, иначе UUID.
   */
  onProjectClick?: (projectKey: string | null, projectId: string) => void;
  /**
   * Может ли текущий пользователь управлять закрепом этой публикации.
   * Если true — в контекстном меню появятся «Закрепить»/«Открепить».
   * Решение принимается родителем (по списку проектов с document:write).
   */
  canPin?: boolean;
  /**
   * Обработчик toggle закрепа. Получает текущее значение (true = сейчас закреплён,
   * хотим открепить; false = не закреплён, хотим закрепить).
   * Родитель отправляет PATCH /meta { isPinned: !item.isPinned } и инвалидирует кэш.
   */
  onTogglePin?: (item: NewsFeedItem) => void;
  /**
   * Phase 9: toggle закладки / «читать позже». Родитель отправляет
   * POST/DELETE /api/bff/me/news/{kind} и инвалидирует кэш ленты.
   */
  onToggleCollection?: (item: NewsFeedItem, kind: 'bookmark' | 'read_later') => void;
  /**
   * P11: свежие счётчики из `useNewsStats` (если есть). Перекрывают
   * baseline из `item.viewCount/commentCount/likeCount` — это позволяет
   * не инвалидировать долгий кэш feed'а после каждого view/like/comment.
   * Если undefined — рендерим baseline.
   */
  liveStats?: NewsStatsItem;
}

/**
 * Карточка одной новости в ленте.
 *
 * Особенности:
 *  - NEW-бейдж пульсирует, если `news_published_at < 24h`.
 *  - Glow вокруг карточки, если `< 1h` (самая свежая публикация подсвечивается).
 *  - Pinned-rib, если `is_pinned = true`.
 *  - Бейдж проекта с цветной иконкой (для cross-project ленты).
 *  - Generated background-gradient по hash заголовка, если у новости нет обложки.
 *
 * См. docs/General_documentation/news-feed-page-spec.md → «Карточка ленты».
 */
function NewsArticleCardImpl({
  item,
  hideProjectBadge,
  variant = 'grid',
  onClick,
  onProjectClick,
  canPin,
  onTogglePin,
  onToggleCollection,
  liveStats,
}: NewsArticleCardProps): React.ReactElement {
  // P11: счётчики берём из live-карты (если есть), fallback на baseline.
  const viewCount = liveStats?.viewCount ?? item.viewCount;
  const commentCount = liveStats?.commentCount ?? item.commentCount;
  const likeCount = liveStats?.likeCount ?? item.likeCount;
  const dislikeCount = liveStats?.dislikeCount ?? item.dislikeCount;
  const [menuOpen, setMenuOpen] = useState(false);
  const publishedAtMs = useMemo(() => {
    const d = dayjs(item.publishedAt);
    return d.isValid() ? d.valueOf() : 0;
  }, [item.publishedAt]);

  const now = Date.now();
  const isNew = publishedAtMs > 0 && now - publishedAtMs < NEW_THRESHOLD_MS;
  const isGlow = publishedAtMs > 0 && now - publishedAtMs < GLOW_THRESHOLD_MS;

  const relativeDate = useMemo(() => {
    if (!publishedAtMs) return '';
    const diff = now - publishedAtMs;
    if (diff < 60_000) return 'только что';
    return dayjs(publishedAtMs).fromNow();
  }, [publishedAtMs, now]);

  /** Детерминированный градиент по hash заголовка (когда нет обложки). */
  const fallbackBg = useMemo(() => {
    if (item.coverUrl) return null;
    const palette = [
      ['#c46a4d', '#5454a1'], // coral → indigo
      ['#5489b5', '#5454a1'], // info → tasks
      ['#51a880', '#4e8992'], // proj → shell
      ['#b08a3e', '#c46a4d'], // gold → coral
      ['#c25151', '#a25636'], // error → news-hover
    ];
    let h = 0;
    for (let i = 0; i < item.title.length; i++) h = (h * 31 + item.title.charCodeAt(i)) | 0;
    const [a, b] = palette[Math.abs(h) % palette.length];
    return `linear-gradient(135deg, ${a}, ${b})`;
  }, [item.coverUrl, item.title]);

  const coverStyle: React.CSSProperties = item.coverUrl
    ? { backgroundImage: `url("${item.coverUrl}")` }
    : { background: fallbackBg ?? '#dde0e6' };

  const authorInitial = useMemo(() => {
    if (!item.author.name) return '?';
    return item.author.name.trim().charAt(0).toUpperCase() || '?';
  }, [item.author.name]);

  const handleClick = (): void => {
    onClick?.(item);
  };

  const handleProjectClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onProjectClick?.(item.projectKey, item.projectId);
  };

  // === Контекстное меню ⋯ ===
  const detailUrl = newsArticleUrl(item.projectKey, item.projectId, item.documentId);
  const fullUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${detailUrl}`
      : detailUrl;

  const menuItems: NonNullable<MenuProps['items']> = [];
  if (canPin) {
    menuItems.push({
      key: 'pin',
      label: item.isPinned ? 'Открепить' : 'Закрепить',
      icon: <span style={{ marginRight: 6 }}>📌</span>,
    });
  }
  menuItems.push(
    {
      key: 'bookmark',
      label: item.isBookmarked ? 'Убрать из закладок' : 'В закладки',
      icon: <span style={{ marginRight: 6 }}>{item.isBookmarked ? '🔖' : '🏷️'}</span>,
    },
    {
      key: 'read_later',
      label: item.isReadLater ? 'Убрать из «Читать позже»' : 'Читать позже',
      icon: <span style={{ marginRight: 6 }}>{item.isReadLater ? '⏰' : '⏱'}</span>,
    },
    { type: 'divider' as const },
    {
      key: 'copy',
      label: 'Скопировать ссылку',
      icon: <span style={{ marginRight: 6 }}>🔗</span>,
    },
    {
      key: 'open',
      label: 'Открыть в Базе знаний',
      icon: <span style={{ marginRight: 6 }}>📖</span>,
    },
  );

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    setMenuOpen(false);
    if (key === 'pin') {
      onTogglePin?.(item);
    } else if (key === 'bookmark') {
      onToggleCollection?.(item, 'bookmark');
    } else if (key === 'read_later') {
      onToggleCollection?.(item, 'read_later');
    } else if (key === 'copy') {
      void navigator.clipboard?.writeText(fullUrl).catch(() => {
        // Тихий fail — clipboard может быть запрещён.
      });
    } else if (key === 'open') {
      window.open(detailUrl, '_self');
    }
  };

  // Лимит рубрик в шапке обложки — две, остальные сворачиваются в +N.
  const visibleCategories = item.categories.slice(0, 2);
  const restCategories = item.categories.length - visibleCategories.length;

  return (
    <article
      className={`${styles.card} ${variant === 'row' ? styles.row : ''} ${
        isGlow ? styles.glow : ''
      }`}
      onClick={handleClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className={styles.cover}>
        <div className={styles.coverBg} style={coverStyle} />
        <div className={styles.coverGradient} />

        {item.isPinned && <div className={styles.pinnedRibbon}>📌 Закреп</div>}
        {isNew && !item.isPinned && <div className={styles.newBadge}>NEW</div>}

        {!hideProjectBadge && (
          <button
            type="button"
            className={styles.projBadge}
            onClick={handleProjectClick}
            title={`Перейти в «${item.projectName}»`}
          >
            <span
              className={styles.pjIco}
              style={{ background: '#5454a1' /* tasks indigo как нейтральный для проекта */ }}
            >
              {item.projectIcon ?? item.projectName.charAt(0).toUpperCase()}
            </span>
            <span>{item.projectName}</span>
          </button>
        )}

        {visibleCategories.length > 0 && (
          <div className={styles.chipLine}>
            {visibleCategories.map((c) => (
              <span key={c.id} className={styles.cat}>
                <span className={styles.dt} style={{ background: c.color }} />
                {c.name}
              </span>
            ))}
            {restCategories > 0 && <span className={styles.cat}>+{restCategories}</span>}
          </div>
        )}

        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={['click']}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          placement="bottomRight"
        >
          <button
            type="button"
            className={`${styles.menuTrigger} ${menuOpen ? styles.open : ''}`}
            aria-label="Действия с публикацией"
            onClick={(e) => e.stopPropagation()}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="6" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="18" r="1.5" />
            </svg>
          </button>
        </Dropdown>
      </div>

      <div className={styles.body}>
        <div className={styles.head}>
          {item.emoji && (
            <span className={styles.emoji}>
              <NewsEmojiInline value={item.emoji} size={20} />
            </span>
          )}
          <h3 className={styles.title}>{item.title || 'Без названия'}</h3>
        </div>

        {item.excerpt && <p className={styles.excerpt}>{item.excerpt}</p>}

        <div className={styles.foot}>
          <div className={styles.author}>
            <span className={styles.avatar}>
              {item.author.avatarUrl ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img src={item.author.avatarUrl} alt="" />
              ) : (
                authorInitial
              )}
            </span>
            {item.author.name && <span className={styles.name}>{item.author.name}</span>}
            <span className={styles.dot} />
            <span className={styles.date}>{relativeDate}</span>
          </div>
          {/* Статы 👁/💬/👍/👎 в подвале карточки (показываем только > 0).
              P11: берём live-значения из `liveStats` (если есть), иначе baseline. */}
          {(viewCount > 0 || commentCount > 0 || likeCount > 0 || dislikeCount > 0) && (
            <div className={styles.stats}>
              {viewCount > 0 && (
                <span className={styles.stat} title="Просмотры">
                  👁 {viewCount}
                </span>
              )}
              {commentCount > 0 && (
                <span className={styles.stat} title="Комментарии">
                  💬 {commentCount}
                </span>
              )}
              {likeCount > 0 && (
                <span className={styles.stat} title="Лайки">
                  👍 {likeCount}
                </span>
              )}
              {dislikeCount > 0 && (
                <span className={styles.stat} title="Дизлайки">
                  👎 {dislikeCount}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export const NewsArticleCard = memo(NewsArticleCardImpl);
